import React from 'react';
import FormSection from './FormSection';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase';

class BusinessSection extends FormSection {
  constructor(props) {
    super(props);
    this.llmService = new LLMService();
    this.state = {
      problemCustomerFlags: []
    };
  }

  validate() {
    const errors = [];
    const { watch } = this.props;
    
    const companyName = watch('companyName');
    const companyWebsite = watch('companyWebsite');
    const incorporationLocation = watch('incorporationLocation');
    const productStage = watch('productStage');
    const problemCustomer = watch('problemCustomer');
    const videoPitchUrl = watch('videoPitchUrl');

    if (!companyName) errors.push('Company name is required');
    // Website is not mandatory anymore
    if (!incorporationLocation) errors.push('Incorporation location is required');
    if (!productStage) errors.push('Product stage is required');
    if (!problemCustomer) errors.push('Problem & Customer description is required');
    if (!videoPitchUrl) errors.push('Video pitch URL is required');

    return errors;
  }

  detectProblemCustomerFlags(description) {
    const flags = [];
    
    if (!description) return flags;
    
    const problemText = description.toLowerCase();
    
    // Check for specific vague examples that should be flagged
    const vagueExamples = [
      'small businesses that need help with marketing',
      'companies that use excel too much',
      'gen z consumers who like technology',
      'people who want an easier life',
      'everyone who wants to be more productive',
      'small businesses that need help',
      'businesses that need help'
    ];
    
    // Check for exact or close matches to vague examples
    for (const example of vagueExamples) {
      if (problemText.includes(example)) {
        flags.push({
          type: 'yellow',
          message: 'Problem & Customer description is too vague → Needs specific role, company size, and pain point'
        });
        return flags;
      }
    }
    
    // Check for vague keywords
    const vagueKeywords = [
      'small businesses',
      'everyone who',
      'people who want',
      'companies that',
      'anyone who',
      'users who like',
      'customers who use'
    ];
    
    const tooGeneralKeywords = [
      'more productive',
      'easier life',
      'better experience',
      'save time',
      'make money',
      'be successful',
      'grow their business'
    ];
    
    let vagueCount = 0;
    for (const keyword of vagueKeywords) {
      if (problemText.includes(keyword)) {
        vagueCount++;
      }
    }
    
    for (const keyword of tooGeneralKeywords) {
      if (problemText.includes(keyword)) {
        vagueCount++;
      }
    }
    
    // Check for specificity indicators
    const hasSpecificRole = /\b(manager|owner|director|founder|ceo|cto|hr|operations|marketing|sales|designer|developer|freelance|consultant|accountant|lawyer|doctor|nurse|teacher|engineer)\b/i.test(problemText);
    const hasSpecificProblem = /\b(struggle|overwhelmed|hard to|difficult|pain|challenge|frustrated|time-consuming|manual|outdated|tracking|scheduling|managing|coordinating|waste|spend|lose)\b/i.test(problemText);
    const hasSpecificContext = /\b(small|medium|startup|enterprise|\d+[-–]\d+|\d+\+|under \d+|company|business|firm|organization|team|department|office|home|school|hospital|clinic|restaurant|store|shop|agency)\b/i.test(problemText);
    
    // Flag if too vague or missing key specificity
    if (vagueCount >= 2 || (!hasSpecificRole && !hasSpecificProblem && !hasSpecificContext)) {
      flags.push({
        type: 'yellow',
        message: 'Problem & Customer description is too vague → Needs specific role, company size, and pain point'
      });
    } else if (problemText.length < 50) {
      flags.push({
        type: 'yellow',
        message: 'Problem & Customer description is too brief → Needs more detail about specific user and problem'
      });
    }
    
    return flags;
  }

  async validateProblemCustomer(description) {
    // This will be called via LLM API to check specificity
    if (!description || description.length < 50) {
      return { isValid: false, reason: 'Description too short' };
    }

    try {
      const result = await this.llmService.validateProblemDescription(description);
      return result;
    } catch (error) {
      console.error('Error validating problem description:', error);
      return { isValid: true, reason: 'Validation service unavailable' };
    }
  }

  handleProblemCustomerChange = async (value) => {
    const { setValue, clearErrors } = this.props;
    setValue('problemCustomer', value);
    clearErrors('problemCustomer');

    // Detect flags immediately
    const flags = this.detectProblemCustomerFlags(value);
    
    // Update flag state
    this.setState({ problemCustomerFlags: flags });
    
    // Also update parent component with flags
    if (this.props.onFlagsChange) {
      this.props.onFlagsChange('problemCustomer', flags);
    }

    // Debounce LLM validation
    if (this.validateTimeout) {
      clearTimeout(this.validateTimeout);
    }

    this.validateTimeout = setTimeout(async () => {
      const validation = await this.validateProblemCustomer(value);
      if (!validation.isValid) {
        // Could set a warning/flag here
        console.log('Problem description may need improvement:', validation.reason);
      }
    }, 2000);
  };

  render() {
    const { phase1Data } = this.props;

    return (
      <div className="border border-gray-200 rounded-lg p-6 mb-6">
        {this.renderSectionHeader('Part 1: Business & Product', 'Tell us about the company you are building.')}
        
        <div className="space-y-6">
          {/* Company Name - Auto-populated */}
          <div>
            {this.renderField({
              name: 'companyName',
              label: 'Company Name',
              type: 'text',
              required: true,
              placeholder: phase1Data.companyName || 'Enter company name'
            })}
            {phase1Data.companyName && (
              <p className="text-sm text-blue-600 mt-1">
                ✓ Auto-populated from Phase 1 application
              </p>
            )}
          </div>

          {/* Company Website - Auto-populated, not mandatory */}
          <div>
            {this.renderField({
              name: 'companyWebsite',
              label: 'Company Website',
              type: 'url',
              required: false,
              placeholder: phase1Data.companyWebsite || 'https://example.com (optional)'
            })}
            {phase1Data.companyWebsite && (
              <p className="text-sm text-blue-600 mt-1">
                ✓ Auto-populated from Phase 1 application
              </p>
            )}
          </div>

          {/* Incorporation Location */}
          {this.renderField({
            name: 'incorporationLocation',
            label: 'Country & State/Province of Incorporation',
            type: 'text',
            required: true,
            placeholder: 'e.g., Delaware, USA'
          })}

          {/* Product Stage */}
          {this.renderField({
            name: 'productStage',
            label: 'Product Stage: How far along are you in building the service or product?',
            type: 'radio',
            required: true,
            options: [
              { value: 'live_paying', label: 'Live with paying customers' },
              { value: 'live_beta', label: 'Live with non-paying/beta users' },
              { value: 'mvp', label: 'Functional prototype (MVP)' },
              { value: 'idea', label: 'Pre-prototype / Idea stage' }
            ]
          })}

          {/* Traction Details */}
          <div>
            {this.renderField({
              name: 'tractionDetails',
              label: 'Traction Details',
              type: 'textarea',
              required: true,
              placeholder: 'Describe your traction, for example: 100 unpaid users within 3 months of beta launch / five paying customers with average ACV of $20K/year / launching MVP in Q3 of this year.'
            })}
            <p className="text-sm text-gray-500 mt-1">
              Be specific about numbers, timeframes, and revenue details.
            </p>
          </div>

          {/* Problem & Customer - LLM Validation */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Problem & Customer <span className="text-red-500">*</span>
            </label>
            <textarea
              {...this.props.register('problemCustomer', { 
                required: 'Problem & Customer description is required'
              })}
              rows={4}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              placeholder="Who is the exact person you're helping, and what problem do they face? Be specific — not just a company or age group, but a real role or situation."
              onChange={(e) => this.handleProblemCustomerChange(e.target.value)}
            />
            <div className="mt-2 text-sm text-gray-500">
              <p className="font-medium">Example:</p>
              <p>"New moms recovering from childbirth who struggle to find time for healthy meals" or "Office managers at small law firms who waste hours ordering supplies."</p>
            </div>
            {this.props.errors.problemCustomer && (
              <p className="mt-1 text-sm text-red-600">{this.props.errors.problemCustomer.message}</p>
            )}
            
            {/* Problem Customer Flags */}
            {this.state.problemCustomerFlags.length > 0 && (
              <div className="mt-3 bg-yellow-50 border border-yellow-200 rounded-md p-3">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h4 className="text-sm font-medium text-yellow-800">
                      Problem Description Flags
                    </h4>
                    <div className="mt-2 text-sm text-yellow-700">
                      <ul className="list-disc list-inside space-y-1">
                        {this.state.problemCustomerFlags.map((flag, index) => (
                          <li key={index}>{flag.message}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Video Pitch */}
          <div>
            {this.renderField({
              name: 'videoPitchUrl',
              label: 'Video Pitch',
              type: 'url',
              required: true,
              placeholder: 'https://youtube.com/watch?v=...',
              validation: {
                pattern: {
                  value: /^https:\/\/(www\.)?(youtube\.com|youtu\.be)/,
                  message: 'Please provide a valid YouTube URL'
                }
              }
            })}
            <p className="text-sm text-gray-500 mt-1">
              Please provide a link to a 1-2 minute unlisted YouTube video where the founding team introduces themselves and what you're building.
            </p>
          </div>
        </div>
      </div>
    );
  }
}

// LLM Service for validation using Firebase Functions
class LLMService {
  constructor() {
    this.validateProblemDescriptionFn = httpsCallable(functions, 'validateProblemDescription');
  }

  async validateProblemDescription(description) {
    try {
      const result = await this.validateProblemDescriptionFn({ description });
      return result.data;
    } catch (error) {
      console.error('Error calling LLM validation function:', error);
      
      // Fallback to basic validation
      return this.basicValidation(description);
    }
  }

  basicValidation(description) {
    const hasSpecificPerson = /\b(manager|director|owner|founder|executive|professional|mom|dad|parent|student|teacher|nurse|doctor|lawyer|accountant|engineer|developer|designer|marketer|salesperson|ceo|cto|cfo|coo)\b/i.test(description);
    const hasSpecificProblem = /\b(struggle|problem|challenge|difficulty|waste|spend|lose|need|want|lack|miss|fail|hard|difficult|impossible|frustrating|annoying|time-consuming|expensive|costly|inefficient|slow|manual|tedious|boring|repetitive)\b/i.test(description);
    const hasSpecificContext = /\b(at|in|for|with|during|while|when|after|before|small|medium|large|startup|company|business|firm|organization|team|department|office|home|school|hospital|clinic|restaurant|store|shop|agency|non-profit|government)\b/i.test(description);
    const wordCount = description.split(/\s+/).length;
    const isLongEnough = wordCount >= 15;
    const isTooGeneric = /\b(people|users|customers|clients|businesses|companies|organizations|everyone|anyone|somebody|someone)\b/i.test(description) && !hasSpecificPerson;
    const isValid = hasSpecificPerson && hasSpecificProblem && hasSpecificContext && isLongEnough && !isTooGeneric;

    let reason = '';
    if (!hasSpecificPerson) reason += 'Need specific person/role. ';
    if (!hasSpecificProblem) reason += 'Need specific problem. ';
    if (!hasSpecificContext) reason += 'Need specific context. ';
    if (!isLongEnough) reason += 'Description too short. ';
    if (isTooGeneric) reason += 'Too generic, be more specific. ';

    return {
      isValid,
      reason: reason.trim(),
      score: isValid ? 100 : 50,
      suggestions: isValid ? [] : [
        "Be more specific about the person/role you're helping",
        "Describe the exact problem they face", 
        "Provide context about where/when this problem occurs"
      ]
    };
  }
}

export default BusinessSection;